/**
 * Party name-based matching system for comparing 2025 results with 2023 historical data.
 * Matches parties by normalized names (case-insensitive, accent-insensitive) instead of unreliable ID codes.
 */

export interface PartyInfo {
  key: string;
  displayName: string;
  color: string;
  nameVariations: string[];
}

/**
 * Main party definitions with colors and name variations.
 * Only tracks the 4 major political forces for comparison.
 */
export const PARTY_DEFINITIONS: Record<string, PartyInfo> = {
  lla: {
    key: "lla",
    displayName: "La Libertad Avanza",
    color: "#6B2C91", // Purple
    nameVariations: [
      "la libertad avanza",
      "alianza la libertad avanza",
      "frente la libertad avanza",
      "transformacion libertaria",
    ],
  },
  fp: {
    key: "fp",
    displayName: "Fuerza Patria",
    color: "#00A3DD", // Light blue
    nameVariations: [
      "fuerza patria",
      "alianza fuerza patria",
      "frente fuerza patria",
      "union por la patria",
      "frente de todos",
      "frente fuerza patria peronista",
    ],
  },
  provincias_unidas: {
    key: "provincias_unidas",
    displayName: "Provincias Unidas",
    color: "#fa691c", // Orange
    nameVariations: [
      "provincias unidas",
      "alianza provincias unidas",
      "alianza defendamos mendoza - provincias unidas",
      "hacemos por nuestro pais",
      "ciudadanos unidos",
    ],
  },
  jxc: {
    key: "jxc",
    displayName: "Juntos por el Cambio",
    color: "#FF9800", // Orange (different shade)
    nameVariations: ["juntos por el cambio", "juntos por el cambio tdf"],
  },
} as const;

/**
 * Remove diacritics (accents) from a string
 */
const removeDiacritics = (value: string) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Normalize party name for matching: lowercase, no accents, trimmed whitespace
 */
export const normalizePartyName = (name: string | undefined | null): string => {
  if (!name) return "";
  return removeDiacritics(name.toLowerCase()).replace(/\s+/g, " ").trim();
};

/**
 * Match a party name to one of our tracked parties by normalized name comparison.
 * Returns the party info if matched, null otherwise.
 */
export function matchPartyByName(
  partyName: string | undefined | null,
): PartyInfo | null {
  if (!partyName) return null;

  const normalized = normalizePartyName(partyName);

  for (const party of Object.values(PARTY_DEFINITIONS)) {
    for (const variation of party.nameVariations) {
      if (
        normalized === normalizePartyName(variation) ||
        normalized.includes(variation)
      ) {
        return party;
      }
    }
  }

  return null;
}
